import Layout from './layout.js';
import DistanceMap from './distanceMap.js';
import RegionDistanceLayers from './regionDistanceLayers.js';
import FilledAmountGetter from './filledAmountGetter.js';
import RegionLayerNearestPoints from './regionLayerNearestPoints.js';
import Direction from './direction.js';
import { Side, SideParams } from './side.js';

const MinimizeOcclusion = true;

// Ln = longitudinal (parallel to callout line direction)
// Tr = transverse (perpendicular to callout line direction)
const getSizeLnKey = (dir) => dir === Direction.VERTICAL ? 'height' : 'width';
const getSizeTrKey = (dir) => dir === Direction.VERTICAL ? 'width' : 'height';
const getPosLnKey = (dir) => dir === Direction.VERTICAL ? 'y' : 'x';
const getPosTrKey = (dir) => dir === Direction.VERTICAL ? 'x' : 'y';
const getPosLnIndex = (dir) => dir === Direction.VERTICAL ? 1 : 0;
const getPosTrIndex = (dir) => dir === Direction.VERTICAL ? 0 : 1;

const getNearestPoint = (regionLayerNearestPoints, id, targetTr, targetLn) => {
    let nearestTr = null, nearestLn = null;
    let nearestDistSq = Infinity;

    const nearestPoints = regionLayerNearestPoints.nearestPoints.get(id);
    if (!nearestPoints) {
        return null;
    }

    for (let [pointTr, pointLn] of nearestPoints.position) {
        let dTr = pointTr-targetTr;
        let dLn = pointLn-targetLn;
        let distSq = dTr*dTr+dLn*dLn;

        if (distSq < nearestDistSq) {
            nearestTr = pointTr;
            nearestLn = pointLn;
            nearestDistSq = distSq;
        }
    }

    return [nearestTr, nearestLn];
}

class BaseSideLayout extends Layout {
    constructor({ side }) {
        super();

        this._side = side;
        this._labels = new Map();
        this._mask = null;
        this._distanceMap = null;
        this._regionDistanceLayers = null;
        if (MinimizeOcclusion) {
            this._filledAmountGetter = null;
        }
        this._regionLayerNearestPoints = null;
    }

    get regionLayerNearestPoints() {
        return this._regionLayerNearestPoints;
    }

    addLabel(id) {
        if (this._labels.has(id)) {
            throw new Error('already added');
        }

        this._labels.set(id, {
            pointPosition: null,
            labelPosition: null,
        });
    }

    removeLabel(id) {
        if (!this._labels.has(id)) {
            throw new Error('not added');
        }

        this._labels.delete(id);
    }

    setLabel(id, { pointPosition, labelPosition }) {
        if (!this._labels.has(id)) {
            throw new Error('not added');
        }

        let data = this._labels.get(id);

        if (pointPosition) {
            const { x, y } = pointPosition;
            pointPosition = { x, y };
        } else {
            pointPosition = null;
        }
        data.pointPosition = pointPosition;

        if (labelPosition) {
            const { x, y } = labelPosition;
            labelPosition = { x, y };
        } else {
            labelPosition = null;
        }
        data.labelPosition = labelPosition;
    }

    setMask(mask) {
        this._mask = mask;

        if (mask) {
            this._pointDepth = Math.floor(mask.width/48);

            if (this._distanceMap === null) {
                this._distanceMap = new DistanceMap();
            }
            this._distanceMap.calculate(mask);

            if (this._regionDistanceLayers === null) {
                this._regionDistanceLayers = new RegionDistanceLayers();
            }
            this._regionDistanceLayers.calculate(mask, this._distanceMap);

            if (MinimizeOcclusion) {
                if (this._filledAmountGetter === null) {
                    this._filledAmountGetter = new FilledAmountGetter(SideParams[this._side].direction);
                }
                this._filledAmountGetter.calculate(mask, this._regionDistanceLayers, null);
            }

            if (this._regionLayerNearestPoints === null) {
                this._regionLayerNearestPoints = new RegionLayerNearestPoints({
                    side: this._side,
                    depth: this._pointDepth,
                });
            } else {
                this._regionLayerNearestPoints.setDepth(this._pointDepth);
            }
            this._regionLayerNearestPoints.calculate(mask, this._regionDistanceLayers);
        } else {
            this._distanceMap = null;
            this._regionDistanceLayers = null;
            if (MinimizeOcclusion) {
                this._filledAmountGetter = null;
            }
            this._regionLayerNearestPoints = null;
        }
    }

    getLabel(id) {
        let data = this._labels.get(id);

        if (!data) {
            return null;
        }

        let { pointPosition, labelPosition } = data;

        if (pointPosition === null || labelPosition === null) {
            return null;
        }

        if (pointPosition) {
            const { x, y } = pointPosition;
            pointPosition = { x, y };
        }

        if (labelPosition) {
            const { x, y } = labelPosition;
            labelPosition = { x, y };
        }

        return { pointPosition, labelPosition };
    }

    update(useMask) {
        if (this._mask === null) {
            for (let [id, data] of this._labels) {
                data.pointPosition = null;
                data.labelPosition = null;
            }

            return;
        }

        const side = this._side;
        const { direction: dir, farSide } = SideParams[side];
        const sizeLnKey = getSizeLnKey(dir), sizeTrKey = getSizeTrKey(dir);
        const posLnKey = getPosLnKey(dir), posTrKey = getPosTrKey(dir);
        const posLnIndex = getPosLnIndex(dir), posTrIndex = getPosTrIndex(dir);

        let emptySpaceWidth;

        if (useMask) {
            const { width, height } = this._mask;
            const getAtPos = this._mask.makeGetterAtPos(), getAtXY = this._mask.makeGetterAtXY();
            const { [sizeLnKey]: sizeLn, [sizeTrKey]: sizeTr } = this._mask;

            emptySpaceWidth = sizeLn;

            if (!MinimizeOcclusion) {

                const minDepths = new Map();
                for (let [id, data] of this._labels) {
                    let regionData = this._regionDistanceLayers.regionData.get(id);

                    if (regionData) {
                        let deepestLayer = regionData.layers.length-1;
                        let minDepth = Math.min(deepestLayer, this._pointDepth);
                        minDepths.set(id, minDepth);
                    }
                }

                for (let [id, data] of this._labels) {
                    let regionData = this._regionDistanceLayers.regionData.get(id);

                    if (regionData) {
                        let minDepth = minDepths.get(id);
                        let points = regionData.layers[minDepth];

                        let pointLn, pointTr;

                        if (data.pointPosition === null) {
                            ({ transverse: pointTr, longitudinal: pointLn } = this._regionLayerNearestPoints.getNearestPoint(id));
                        } else {
                            const { pointPosition } = data;
                            const { [posLnKey]: oldLn, [posTrKey]: oldTr } = pointPosition;

                            const { x: oldX, y: oldY } = pointPosition;
                            let oldPos = oldY*width+oldX;

                            if (getAtPos(oldPos) !== id || this._distanceMap.data[oldPos] < minDepth) {
                                let [targetLn, targetTr] = [farSide ? sizeLn : 0, pointPosition[posTrKey]];
                                [pointTr, pointLn] = getNearestPoint(this._regionLayerNearestPoints, id, targetLn, targetTr);
                            } else {
                                [pointLn, pointTr] = [oldLn, oldTr];
                            }
                        }

                        let objEdgeDist;
                        let scanPoint = [];
                        scanPoint[posTrIndex] = pointTr;
                        if (!farSide) {
                            scanPoint[posLnIndex] = 0;
                            while (scanPoint[posLnIndex] < sizeLn && getAtXY(scanPoint[0], scanPoint[1]) === null) {
                                scanPoint[posLnIndex]++;
                            }
                            objEdgeDist = scanPoint[posLnIndex];
                        } else {
                            scanPoint[posLnIndex] = sizeLn-1;
                            while (scanPoint[posLnIndex] < sizeLn && getAtXY(scanPoint[0], scanPoint[1]) === null) {
                                // sizeLn-objEdgeDist-1 === scanPoint[posLnIndex]
                                scanPoint[posLnIndex]--;
                            }
                            objEdgeDist = sizeLn-1-scanPoint[posLnIndex];
                        }
                        emptySpaceWidth = Math.min(emptySpaceWidth, objEdgeDist);

                        data.pointPosition = { [posLnKey]: pointLn, [posTrKey]: pointTr };
                    } else {
                        data.pointPosition = null;
                    }
                }

            } else {

                for (let [id, data] of this._labels) {
                    let pointLn, pointTr = null;
                    let leastOcclusion = Infinity;

                    const nearestPoints = this._regionLayerNearestPoints.nearestPoints.get(id);

                    if (nearestPoints) {
                        for (let [posTr, posLn] of nearestPoints.position) {
                            let scanLength;
                            let filledAmount;
                            if (!farSide) {
                                scanLength = posLn-0;
                                filledAmount = this._filledAmountGetter.getFilledAmount(posTr, 0, posLn);
                            } else {
                                scanLength = sizeLn-(posLn+1);
                                filledAmount = this._filledAmountGetter.getFilledAmount(posTr, posLn+1, sizeLn);
                            }
                            const emptyAmount = scanLength-filledAmount;
                            let occlusion = filledAmount*5+emptyAmount;

                            if (occlusion < leastOcclusion) {
                                leastOcclusion = occlusion;
                                pointLn = posLn;
                                pointTr = posTr;
                            }
                        }
                    }

                    if (pointTr !== null) {
                        emptySpaceWidth = Math.min(emptySpaceWidth, this._filledAmountGetter.getEmptyLengthAtEdge(pointTr, farSide));
                        data.pointPosition = { [posLnKey]: pointLn, [posTrKey]: pointTr };
                    } else {
                        data.pointPosition = null;
                    }
                }

            }
        } else {
            emptySpaceWidth = Infinity;

            const sizeLn = this._mask[sizeLnKey];

            for (let [id, { pointPosition }] of this._labels) {
                if (pointPosition !== null) {
                    if (!farSide) {
                        emptySpaceWidth = Math.min(emptySpaceWidth, pointPosition[posLnKey]);
                    } else {
                        emptySpaceWidth = Math.min(emptySpaceWidth, sizeLn-1-pointPosition[posLnKey]);
                    }
                }
            }
        }

        for (let [id, data] of this._labels) {
            const sizeLn = this._mask[sizeLnKey];

            if (data.pointPosition !== null) {
                let posTr = data.pointPosition[posTrKey];

                if (!farSide) {
                    data.labelPosition = { [posLnKey]: emptySpaceWidth/2, [posTrKey]: posTr };
                } else {
                    data.labelPosition = { [posLnKey]: sizeLn-emptySpaceWidth/2, [posTrKey]: posTr };
                }
            } else {
                data.labelPosition = null;
            }
        }

    }

    // debug
    generateDistanceImageData() {
        return this._distanceMap.generateImageData();
    }
}

export default BaseSideLayout;
export {
    BaseSideLayout,
    getSizeLnKey, getSizeTrKey, getPosLnKey, getPosTrKey, getPosLnIndex, getPosTrIndex,
};
